import { BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { Pressable, StyleSheet, View } from "react-native";
import { useState } from "react";

import { NudgeCourseRefType } from "@axio/graphql/generated/graphql";
import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale } from "../../../styles/design-system";

export type CourseNudgePreferenceItem = {
  courseId: string;
  courseRefType: NudgeCourseRefType;
  courseName: string;
  courseCode?: string | null;
  instructorName?: string | null;
  enabled: boolean;
  courseUpdatesEnabled: boolean;
  facultyUpdatesEnabled: boolean;
};

type CourseNudgePreferenceToggleInput = {
  courseId: string;
  courseRefType: NudgeCourseRefType;
  enabled: boolean;
  courseUpdatesEnabled: boolean;
  facultyUpdatesEnabled: boolean;
};

type CourseNudgePreferencesProps = {
  courses: CourseNudgePreferenceItem[];
  loading: boolean;
  initialExpandedCourseKey?: string | null;
  onToggleCourse: (input: CourseNudgePreferenceToggleInput) => void;
};

const SKELETON_ROWS = ["course-nudge-skeleton-1", "course-nudge-skeleton-2", "course-nudge-skeleton-3"];

const getCourseInitial = (courseName: string): string => {
  const trimmedName = courseName.trim();
  return trimmedName.length > 0 ? trimmedName[0].toUpperCase() : "?";
};

const getNextCourseSubtypeState = (course: CourseNudgePreferenceItem, enabled: boolean) => {
  if (!enabled) {
    return {
      courseUpdatesEnabled: course.courseUpdatesEnabled,
      facultyUpdatesEnabled: course.facultyUpdatesEnabled,
    };
  }

  const hasSelectedSubtype = course.courseUpdatesEnabled || course.facultyUpdatesEnabled;

  return {
    courseUpdatesEnabled: hasSelectedSubtype ? course.courseUpdatesEnabled : true,
    facultyUpdatesEnabled: hasSelectedSubtype ? course.facultyUpdatesEnabled : true,
  };
};

export const CourseNudgePreferences = ({
  courses,
  loading,
  initialExpandedCourseKey = null,
  onToggleCourse,
}: CourseNudgePreferencesProps) => {
  const [expandedCourseKey, setExpandedCourseKey] = useState<string | null>(initialExpandedCourseKey);
  const enabledCount = courses.filter((course) => course.enabled).length;

  return (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeaderContent}>
        <View style={styles.sectionHeaderRow}>
          <View style={styles.sectionTitleGroup}>
            <BookOpen size={18} color={colors.grey[500]} aria-hidden={true} />
            <CustomText text="Notifications by course" useTranslationText={false} style={styles.sectionHeaderTitle} />
          </View>
          {!loading && courses.length > 0 ? (
            <CustomText
              text={`${enabledCount} of ${courses.length} on`}
              useTranslationText={false}
              style={styles.enabledCount}
            />
          ) : null}
        </View>
        <CustomText
          text="Choose which courses can notify you — and which updates you get from each."
          useTranslationText={false}
          style={styles.sectionSubtitle}
        />
      </View>

      {loading ? (
        <View style={styles.courseList}>
          {SKELETON_ROWS.map((rowKey) => (
            <View key={rowKey} style={styles.courseRow}>
              <View style={styles.skeletonAvatar} />
              <View style={styles.skeletonTextGroup}>
                <View style={styles.skeletonTitle} />
                <View style={styles.skeletonSubtitle} />
              </View>
              <View style={styles.skeletonSwitch} />
            </View>
          ))}
        </View>
      ) : courses.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText text="No enrolled courses found." useTranslationText={false} style={styles.emptyStateText} />
        </View>
      ) : (
        <View style={styles.courseList}>
          {courses.map((course) => {
            const courseKey = `${course.courseRefType}:${course.courseId}`;
            const isExpanded = expandedCourseKey === courseKey;
            const instructorLabel = course.instructorName ?? course.courseCode ?? null;
            const courseUpdatesChecked = course.courseUpdatesEnabled;
            const facultyUpdatesChecked = course.facultyUpdatesEnabled;
            const detailTitleStyle = course.enabled
              ? styles.detailTitle
              : [styles.detailTitle, styles.detailTitleMuted];
            const detailDescriptionStyle = course.enabled
              ? styles.detailDescription
              : [styles.detailDescription, styles.detailDescriptionMuted];

            return (
              <View key={courseKey}>
                <Pressable
                  role="button"
                  accessibilityState={{ expanded: isExpanded }}
                  accessibilityLabel={`${course.courseName} notification details ${isExpanded ? "collapse" : "expand"}`}
                  onPress={() => setExpandedCourseKey((previousKey) => (previousKey === courseKey ? null : courseKey))}
                  style={({ hovered }) => [styles.courseRow, hovered && styles.courseRowHover]}>
                  <View style={styles.courseAvatar}>
                    <CustomText
                      text={getCourseInitial(course.courseName)}
                      useTranslationText={false}
                      style={styles.avatarText}
                    />
                  </View>
                  <View style={styles.courseText}>
                    <CustomText text={course.courseName} useTranslationText={false} style={styles.courseName} />
                    {instructorLabel ? (
                      <CustomText text={instructorLabel} useTranslationText={false} style={styles.courseCode} />
                    ) : null}
                  </View>
                  <View style={styles.courseControls}>
                    <CustomSwitch
                      value={course.enabled}
                      onPress={(event) => {
                        event.stopPropagation();
                      }}
                      onValueChange={(enabled) => {
                        const nextSubtypeState = getNextCourseSubtypeState(course, enabled);

                        onToggleCourse({
                          courseId: course.courseId,
                          courseRefType: course.courseRefType,
                          enabled,
                          courseUpdatesEnabled: nextSubtypeState.courseUpdatesEnabled,
                          facultyUpdatesEnabled: nextSubtypeState.facultyUpdatesEnabled,
                        });
                      }}
                      accessibilityLabel={`${course.courseName} nudges`}
                    />
                    <View style={styles.expandIcon}>
                      {isExpanded ? (
                        <ChevronUp size={18} color={colors.grey[500]} aria-hidden={true} />
                      ) : (
                        <ChevronDown size={18} color={colors.grey[500]} aria-hidden={true} />
                      )}
                    </View>
                  </View>
                </Pressable>

                {isExpanded ? (
                  <View style={styles.detailPanel}>
                    <View style={styles.detailRow}>
                      <View style={styles.detailText}>
                        <CustomText text="Course updates" useTranslationText={false} style={detailTitleStyle} />
                        <CustomText
                          text="Homework, tests, and project deadlines"
                          useTranslationText={false}
                          style={detailDescriptionStyle}
                        />
                      </View>
                      <CustomSwitch
                        value={courseUpdatesChecked}
                        disabled={!course.enabled}
                        onValueChange={(courseUpdatesEnabled) => {
                          onToggleCourse({
                            courseId: course.courseId,
                            courseRefType: course.courseRefType,
                            enabled: courseUpdatesEnabled || facultyUpdatesChecked,
                            courseUpdatesEnabled,
                            facultyUpdatesEnabled: facultyUpdatesChecked,
                          });
                        }}
                        accessibilityLabel={`${course.courseName} course updates`}
                      />
                    </View>
                    <View style={styles.detailRow}>
                      <View style={styles.detailText}>
                        <CustomText text="Faculty updates" useTranslationText={false} style={detailTitleStyle} />
                        <CustomText
                          text="Grades, feedback, and announcements"
                          useTranslationText={false}
                          style={detailDescriptionStyle}
                        />
                      </View>
                      <CustomSwitch
                        value={facultyUpdatesChecked}
                        disabled={!course.enabled}
                        onValueChange={(facultyUpdatesEnabled) => {
                          onToggleCourse({
                            courseId: course.courseId,
                            courseRefType: course.courseRefType,
                            enabled: courseUpdatesChecked || facultyUpdatesEnabled,
                            courseUpdatesEnabled: courseUpdatesChecked,
                            facultyUpdatesEnabled,
                          });
                        }}
                        accessibilityLabel={`${course.courseName} faculty updates`}
                      />
                    </View>
                    {!course.enabled ? (
                      <CustomText
                        text={`${course.courseName} is muted. Turn it back on to choose which updates you receive.`}
                        useTranslationText={false}
                        style={styles.mutedCourseNotice}
                      />
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    overflow: "hidden",
  },
  sectionHeaderContent: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  sectionTitleGroup: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  sectionHeaderTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
  },
  enabledCount: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  sectionSubtitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginTop: spacing.xxs,
  },
  courseList: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  courseRow: {
    minHeight: 74,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  courseRowHover: {
    backgroundColor: colors.grey[25],
  },
  courseAvatar: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.blue[100],
  },
  avatarText: {
    color: colors.blue[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "700",
  },
  courseText: {
    flex: 1,
    gap: spacing.xxs,
  },
  courseName: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  courseCode: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  courseControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginLeft: spacing.sm,
  },
  expandIcon: {
    width: spacing.lg,
    height: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  detailPanel: {
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  detailRow: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  detailText: {
    flex: 1,
    gap: spacing.xxs,
  },
  detailTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  detailTitleMuted: {
    color: colors.grey[400],
  },
  detailDescription: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  detailDescriptionMuted: {
    color: colors.grey[400],
  },
  mutedCourseNotice: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  emptyState: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  emptyStateText: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  skeletonAvatar: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.full,
    backgroundColor: colors.grey[100],
  },
  skeletonTextGroup: {
    flex: 1,
    gap: spacing.xs,
  },
  skeletonTitle: {
    width: 180,
    maxWidth: "70%",
    height: 14,
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
  },
  skeletonSubtitle: {
    width: 120,
    maxWidth: "50%",
    height: 12,
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
  },
  skeletonSwitch: {
    width: 52,
    height: 28,
    borderRadius: radii.full,
    backgroundColor: colors.grey[100],
  },
});
